import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
    useInvoicingSelectionContext,
    SelectionStatus,
} from '../../Contexts/InvoicingSelectionContext';

const ShiftSelectionContext = createContext();

export const useShiftSelectionContext = () => {
    const context = useContext(ShiftSelectionContext);
    if (!context) {
        throw new Error(
            'useShiftSelectionContext must be used within a ShiftSelectionContextProvider'
        );
    }
    return context;
};

export const useEnhancedShiftSelectionContext = ({ disableSelection, bookings, filterFn }) => {
    const context = useContext(ShiftSelectionContext);
    const invoicingSelectionContext = useInvoicingSelectionContext();
    if (!context || !invoicingSelectionContext) {
        throw new Error(
            'useShiftSelectionContext must be used within a ShiftSelectionContextProvider and InvoicingSelectionContextProvider'
        );
    }

    const { handleSelectBooking, handleSetSelectBookingChunk, getBookingSelectionState } =
        invoicingSelectionContext;
    const { isBookingAnchor, isBookingAnchored, setBookingAnchor } = context;

    const handleSelectBookingClick = useCallback(
        (bookingId, event) => {
            if (disableSelection) return;
            const isShiftClick = !!event?.shiftKey;

            // Single click (or no anchor yet) -> keep existing toggle behavior.
            if (!isShiftClick || !isBookingAnchor || isBookingAnchored(bookingId)) {
                handleSelectBooking(bookingId);
                setBookingAnchor(bookingId);
                return;
            }

            const startIndex = bookings.findIndex((b) => isBookingAnchored(b.bookingId));
            const endIndex = bookings.findIndex((b) => b.bookingId === bookingId);
            // If either id isn't on the current page/list, fall back to single click behavior.
            if (startIndex === -1 || endIndex === -1) {
                handleSelectBooking(bookingId);
                setBookingAnchor(bookingId);
                return;
            }

            const [from, to] =
                startIndex < endIndex ? [startIndex, endIndex] : [endIndex, startIndex];
            const bookingIdsInRange = bookings
                .slice(from, to + 1)
                .filter(filterFn || (() => true))
                .map((b) => b.bookingId);

            const clickedStatus = getBookingSelectionState(bookingId);
            const newStatus =
                clickedStatus === SelectionStatus.SELECTED
                    ? SelectionStatus.NOT_SELECTED
                    : SelectionStatus.SELECTED;

            handleSetSelectBookingChunk(bookingIdsInRange, newStatus);
            setBookingAnchor(bookingId);
        },
        [
            bookings,
            disableSelection,
            handleSelectBooking,
            getBookingSelectionState,
            isBookingAnchor,
            isBookingAnchored,
            handleSetSelectBookingChunk,
        ]
    );

    return useMemo(
        () => ({ ...context, handleSelectBookingClick }),
        [context, handleSelectBookingClick]
    );
};

export const ShiftSelectionContextProvider = ({ children }) => {
    const [bookingId, setBookingId] = useState(null);
    const [itemId, setItemId] = useState(null);

    const isBookingAnchor = !!bookingId && !itemId;
    const isItemAnchor = !!bookingId && !!itemId;

    const isBookingAnchored = useCallback((bId) => bId === bookingId, [bookingId]);
    const isItemAnchored = useCallback(
        (bId, iId) => isItemAnchor && bId === bookingId && iId === itemId,
        [isItemAnchor, bookingId, itemId]
    );

    const resetAnchor = useCallback(() => {
        setBookingId(null);
        setItemId(null);
    }, []);
    const setBookingAnchor = useCallback((bId) => {
        setBookingId(bId);
        setItemId(null);
    }, []);
    const setItemAnchor = useCallback((bId, iId) => {
        setBookingId(bId);
        setItemId(iId);
    }, []);

    const memoizedContextValue = useMemo(
        () => ({
            isBookingAnchored,
            isItemAnchored,
            isBookingAnchor,
            isItemAnchor,
            setBookingAnchor,
            setItemAnchor,
            resetAnchor,
        }),
        [
            isBookingAnchored,
            isItemAnchored,
            isBookingAnchor,
            isItemAnchor,
            setBookingAnchor,
            setItemAnchor,
            resetAnchor,
        ]
    );

    return (
        <ShiftSelectionContext.Provider value={memoizedContextValue}>
            {children}
        </ShiftSelectionContext.Provider>
    );
};
